"use client";

/**
 * P3-09 · Buy-box secondary actions.
 *
 * Flag-gated (hidden entirely when off) and rendered as an `aria-pressed`
 * toggle so the saved state is announced. Wishlist persists — server for
 * signed-in shoppers, localStorage for guests, merged on sign-in (see
 * {@link useWishlist}).
 *
 * P14-10 · Compare and save-for-later were removed rather than wired. Both
 * were three phases old, both wrote a localStorage key nothing has ever read,
 * and neither had a route, a backend or a recorded request behind it: there is
 * no `/compare` page for a comparison to be shown on, and save-for-later's own
 * cart-page note said its surface "ships in its own ticket", which it never
 * did. Deleting them is the honest option — the flags said `false` and the
 * buttons said "Comparing", and only one of those was true. A future ticket
 * can build either from a spec, which is what neither had.
 */
import * as React from "react";

import { useFeatureFlag } from "~/components/feature-flags/provider";
import { useToast } from "~/components/ui/toast";
import { useWishlist } from "~/lib/wishlist/use-wishlist";
import { cn } from "~/lib/utils";

function ToggleActionButton({
  active,
  onToggle,
  idleLabel,
  activeLabel,
  icon,
  testId,
}: {
  active: boolean;
  onToggle: () => void;
  idleLabel: string;
  activeLabel: string;
  icon: React.ReactNode;
  testId: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      data-testid={testId}
      onClick={onToggle}
      className={cn(
        "inline-flex items-center gap-2 rounded-[var(--btn-radius)] border px-3 py-2 text-sm transition-colors",
        active
          ? "border-fg text-fg"
          : "border-border text-fg-muted hover:border-fg hover:text-fg",
      )}
    >
      {icon}
      <span>{active ? activeLabel : idleLabel}</span>
    </button>
  );
}

const iconProps = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg {...iconProps} fill={filled ? "currentColor" : "none"}>
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />
    </svg>
  );
}



function WishlistButton({
  productId,
  signedIn,
}: {
  productId: string;
  signedIn: boolean;
}) {
  const on = useFeatureFlag("wishlist");
  const wishlist = useWishlist({ signedIn, enabled: on });
  const { toast } = useToast();
  if (!on) return null;

  const saved = wishlist.has(productId);
  return (
    <ToggleActionButton
      active={saved}
      testId="pdp-wishlist"
      idleLabel="Wishlist"
      activeLabel="Wishlisted"
      icon={<HeartIcon filled={saved} />}
      onToggle={() => {
        const next = wishlist.toggle(productId);
        toast({
          title: next ? "Added to wishlist" : "Removed from wishlist",
          tone: "default",
        });
      }}
    />
  );
}



export function SecondaryActions({
  productId,
  signedIn,
  className,
}: {
  productId: string;
  signedIn: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn("flex flex-wrap gap-2", className)}
      data-testid="pdp-secondary-actions"
    >
      <WishlistButton productId={productId} signedIn={signedIn} />
    </div>
  );
}
